import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { DataTable, type Column } from '../components/DataTable';
import { hierarchy, apiKeys, type Client, type ApiKeyRecord, type ListParams, ApiError } from '../lib/api';
import { CampaignTable } from './Campaigns';

const columns: Column<Client>[] = [
  { key: 'name', label: 'Client', sortField: 'company_name', render: (c) => <strong>{c.companyName}</strong> },
  { key: 'email', label: 'Email', sortField: 'email', render: (c) => c.email },
  { key: 'city', label: 'City', sortField: 'city', render: (c) => c.city },
  { key: 'state', label: 'State', width: '5rem', render: (c) => c.state },
  { key: 'active', label: 'Status', width: '6rem',
    render: (c) => <span className={c.isActive ? 'pill ok' : 'pill off'}>{c.isActive ? 'active' : 'inactive'}</span> },
];

export function ClientsScreen() {
  const navigate = useNavigate();
  const load = useCallback((p: ListParams) => hierarchy.clients(p), []);
  return (
    <DataTable
      title="Clients"
      columns={columns}
      load={load}
      initialSort="company_name"
      initialDirection="asc"
      searchPlaceholder="Search clients…"
      onRowClick={(c) => navigate(`/clients/${c.id}`)}
    />
  );
}

export function ClientDetailScreen() {
  const { id = '' } = useParams();
  const [client, setClient] = useState<Client | null>(null);

  useEffect(() => {
    if (!id) return;
    let live = true;
    hierarchy.client(id).then((c) => { if (live) setClient(c); }).catch(() => { /* client row may be gone */ });
    return () => { live = false; };
  }, [id]);

  return (
    <>
      <nav className="crumbs">
        <Link to="/clients">Clients</Link> <span>/</span> <span>{client?.companyName ?? 'Client'}</span>
      </nav>

      <div className="client-head">
        <div>
          <h1 style={{ margin: 0 }}>{client?.companyName ?? 'Client'}</h1>
          {client && (
            <p className="sub" style={{ margin: '0.25rem 0 0' }}>
              {[client.email, client.city, client.state].filter(Boolean).join(' · ') || '—'}
              {' '}<span className={client.isActive ? 'pill ok' : 'pill off'}>{client.isActive ? 'active' : 'inactive'}</span>
            </p>
          )}
        </div>
        <Link className="button" to={`/clients/${id}/edit`}>Edit client</Link>
      </div>

      <ClientApiKeys clientId={id} />

      {/* The same table as the campaigns screen, so the columns and metrics cannot drift. */}
      <CampaignTable title="Campaigns" scope={{ clientId: id }} />
    </>
  );
}

/**
 * The client's public-API keys: list, add, rotate, revoke.
 *
 * The raw key is returned by the server exactly once (on issue/rotate); it is shown here immediately in a
 * one-time banner and never again — the list only ever carries the non-secret prefix.
 */
function ClientApiKeys({ clientId }: { clientId: string }): JSX.Element {
  const [keys, setKeys] = useState<ApiKeyRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState('');
  const [revealed, setRevealed] = useState<{ raw: string; label?: string } | null>(null);

  const refresh = useCallback(() => {
    apiKeys.list(clientId)
      .then(setKeys)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Could not load API keys.'));
  }, [clientId]);

  useEffect(() => { if (clientId) refresh(); }, [clientId, refresh]);

  const add = useCallback(async () => {
    setError(null); setBusy(true);
    try {
      const { apiKey } = await apiKeys.issue(clientId, label.trim() || undefined);
      setRevealed({ raw: apiKey, label: label.trim() || undefined });
      setLabel('');
      refresh();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not create the key.'); }
    finally { setBusy(false); }
  }, [clientId, label, refresh]);

  const rotate = useCallback(async (k: ApiKeyRecord) => {
    if (!window.confirm(`Rotate this key? The old key (${k.keyPrefix}…) stops working immediately.`)) return;
    setError(null); setBusy(true);
    try {
      const { apiKey } = await apiKeys.rotate(clientId, k.id, k.label);
      setRevealed({ raw: apiKey, label: k.label });
      refresh();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not rotate the key.'); }
    finally { setBusy(false); }
  }, [clientId, refresh]);

  const revoke = useCallback(async (k: ApiKeyRecord) => {
    if (!window.confirm(`Revoke this key (${k.keyPrefix}…)? This cannot be undone.`)) return;
    setError(null); setBusy(true);
    try { await apiKeys.revoke(k.id); refresh(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not revoke the key.'); }
    finally { setBusy(false); }
  }, [refresh]);

  return (
    <section className="keys-panel">
      <h2>API keys</h2>
      <p className="sub">Per-client keys for the public reporting API. A key is scoped to this client only.</p>
      {error && <div className="banner error" style={{ marginBottom: '0.75rem' }}>{error}</div>}

      {revealed && (
        <div className="banner ok" style={{ marginBottom: '0.75rem' }}>
          <strong>New key{revealed.label ? ` “${revealed.label}”` : ''} — copy it now, it won’t be shown again:</strong>
          <div style={{ marginTop: '0.4rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <code style={{ userSelect: 'all', wordBreak: 'break-all', flex: 1 }}>{revealed.raw}</code>
            <button className="button" onClick={() => navigator.clipboard?.writeText(revealed.raw)}>Copy</button>
            <button className="button" onClick={() => setRevealed(null)}>Dismiss</button>
          </div>
        </div>
      )}

      {keys === null ? <div className="busy">Loading…</div> : (
        keys.length === 0 ? <p className="sub">No keys yet.</p> : (
          <table className="keys-table">
            <thead><tr><th>Key</th><th>Label</th><th>Created</th><th>Last used</th><th /></tr></thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id}>
                  <td><code>{k.keyPrefix}…</code></td>
                  <td>{k.label || <span className="sub">—</span>}</td>
                  <td>{k.createdAt ? new Date(k.createdAt).toLocaleDateString() : '—'}</td>
                  <td>{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : <span className="sub">never</span>}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="button" disabled={busy} onClick={() => rotate(k)}>Rotate</button>{' '}
                    <button className="button danger" disabled={busy} onClick={() => revoke(k)}>Revoke</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      <div className="keys-add">
        <input
          type="text" placeholder="Label (optional, e.g. “Acme integration”)"
          value={label} onChange={(e) => setLabel(e.target.value)}
        />
        <button className="button primary" disabled={busy} onClick={add}>{busy ? 'Working…' : 'Add key'}</button>
      </div>
    </section>
  );
}
